import React, { useState, useEffect } from 'react';
import { auth, getUserListings, getCalendarData, setCalendarDate } from '../firebase';
import './HostingCalendar.css';

function HostingCalendar() {
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateModal, setDateModal] = useState(false);
  const [dateForm, setDateForm] = useState({ price: '', blocked: false });

  useEffect(() => {
    fetchUserListings();
  }, []);

  useEffect(() => {
    if (selectedListing) {
      fetchCalendarData(selectedListing);
    }
  }, [selectedListing]);

  const fetchUserListings = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userListings = await getUserListings(user.uid);
      setListings(userListings);
      if (userListings.length > 0) {
        setSelectedListing(userListings[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    }
  };

  const fetchCalendarData = async (listingId) => {
    try {
      const data = await getCalendarData(listingId, null, null);
      setCalendarData(data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    }
  };

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const handleDateClick = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(date);
    const dateStr = date.toISOString().split('T')[0];
    const existing = calendarData[dateStr] || { price: null, blocked: false };
    setDateForm({ price: existing.price || '', blocked: existing.blocked || false });
    setDateModal(true);
  };

  const handleSaveDate = async () => {
    if (!selectedDate || !selectedListing) return;
    
    try {
      const user = auth.currentUser;
      if (!user) return;

      const dateStr = selectedDate.toISOString().split('T')[0];
      const data = dateForm.blocked 
        ? { blocked: true, price: null }
        : dateForm.price ? { blocked: false, price: parseInt(dateForm.price) } : null;

      await setCalendarDate(selectedListing, user.uid, dateStr, data || {});

      const newData = { ...calendarData };
      if (data) {
        newData[dateStr] = data;
      } else {
        delete newData[dateStr];
      }
      setCalendarData(newData);
      setDateModal(false);
      setSelectedDate(null);
    } catch (error) {
      alert('Failed to save date: ' + error.message);
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const getDayData = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = date.toISOString().split('T')[0];
    return calendarData[dateStr] || { blocked: false, price: null };
  };

  const renderCalendar = () => {
    const days = [];
    const totalDays = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="cal-empty"></div>);
    }

    for (let day = 1; day <= totalDays; day++) {
      const data = getDayData(day);
      days.push(
        <div
          key={day}
          className={`cal-day ${data.blocked ? 'blocked' : ''} ${data.price ? 'priced' : ''}`}
          onClick={() => handleDateClick(day)}
        >
          <div className="cal-day-number">{day}</div>
          {data.blocked && <div className="cal-blocked">❌</div>}
          {data.price && <div className="cal-price">${data.price}</div>}
        </div>
      );
    }

    return days;
  };

  const monthYear = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const selectedListingData = listings.find(l => l.id === selectedListing);

  return (
    <div className="hosting-calendar-container">
      <div className="calendar-sidebar">
        <h3>Select Listing</h3>
        <div className="listings-list">
          {listings.map(listing => (
            <button
              key={listing.id}
              className={`listing-btn ${selectedListing === listing.id ? 'active' : ''}`}
              onClick={() => setSelectedListing(listing.id)}
            >
              <div className="listing-name">{listing.title}</div>
              <div className="listing-price">${listing.price}/mo</div>
            </button>
          ))}
        </div>
      </div>

      <div className="calendar-main">
        {selectedListingData && (
          <>
            <div className="calendar-info">
              <h2>{selectedListingData.title}</h2>
              <p>Base price: ${selectedListingData.price}/month</p>
            </div>

            <div className="cal-header">
              <button onClick={handlePrevMonth} className="cal-nav">←</button>
              <h3>{monthYear}</h3>
              <button onClick={handleNextMonth} className="cal-nav">→</button>
            </div>

            <div className="cal-weekdays">
              <div>Sun</div>
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
            </div>

            <div className="cal-grid">
              {renderCalendar()}
            </div>

            <div className="cal-legend">
              <div className="legend-item">
                <span className="legend-block">❌</span> Blocked
              </div>
              <div className="legend-item">
                <span className="legend-price">💰</span> Custom Price
              </div>
              <div className="legend-item">
                <span className="legend-open">⭕</span> Available (Default)
              </div>
            </div>
          </>
        )}
      </div>

      {dateModal && (
        <div className="modal-overlay" onClick={() => setDateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Set Availability for {selectedDate?.toLocaleDateString()}</h3>
            
            <div className="modal-options">
              <label>
                <input
                  type="radio"
                  name="dateType"
                  checked={dateForm.blocked}
                  onChange={() => setDateForm({ ...dateForm, blocked: true })}
                />
                <span>❌ Block This Date</span>
              </label>
              
              <label>
                <input
                  type="radio"
                  name="dateType"
                  checked={!dateForm.blocked}
                  onChange={() => setDateForm({ ...dateForm, blocked: false })}
                />
                <span>💰 Set Custom Price</span>
              </label>
            </div>

            {!dateForm.blocked && (
              <input
                type="number"
                placeholder="Enter price for this night"
                value={dateForm.price}
                onChange={(e) => setDateForm({ ...dateForm, price: e.target.value })}
                className="modal-input"
                min="0"
              />
            )}

            <div className="modal-actions">
              <button onClick={() => setDateModal(false)} className="modal-cancel">Cancel</button>
              <button onClick={handleSaveDate} className="modal-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HostingCalendar;
